import React, { useRef, useState } from 'react';
import {
    ScrollablePane,
    Stack,
    IStackStyles,
    TextField,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Selection,
    DefaultButton
} from "@fluentui/react";
import { DataGrid, ErrorStateComponent, IErrorStateComponentProps } from '@coherence-design-system/controls';
import { useBoolean } from '@fluentui/react-hooks';
import { columns, Role } from '../../DataModels/Role';
import { SearchUserResult } from '../../DataModels/SearchUserResult';
import { IAMService } from '../../Services/IAMService';
import { SearchUserNotFoundError } from '../../DataModels/Errors/SearchUserNotFoundError';
import { ErrorScreenTemplate } from '../../DataModels/ErrorScreenTemplate';
import { IGenericDialogTemplate } from '../../DataModels/GenericDialogTemplate';
import { GenericResponseError } from '../../DataModels/Errors/GenericResponseError';
import { ActionConfirmationDialog } from '../../Components/ActionConfirmationDialog';
import * as SiteStyle from '../Styles';
import { RequestConfirmationModal } from '../../Components/RequestConfirmationModal';
import { AccessRequestResult } from '../../DataModels/AccessRequestResult';
import { AdminPortalFooter } from '../../AdminPortalFooter';

const tokens = {
    stackTokens: {
        childrenGap: 20,
        maxHeight: 100,
    },
    spinnerStack: {
        childrenGap: 20,
    },
};
const stackStyles: Partial<IStackStyles> = { root: { justifyContent: 'left', alignItems: 'left', paddingLeft: 10, paddingRight: 10 } };
const buttonStackStyles: Partial<IStackStyles> = { root: { paddingBottom: 40, paddingTop: 40 } };
// Removed unused gridStackStyles to resolve the compile error.
const emailInputBoxStyle = { minWidth: 250 };


interface IUserManagementHomePageProps {
    iamService: IAMService
}

export function UserManagementHomePage(props: IUserManagementHomePageProps) {
    const [shouldShowIndicator, setShowIndicator] = useState(false);
    const [userBaseInformation, setUserInformation] = useState<SearchUserResult>();
    const [roleList, setRoleList] = useState<Role[]>([]);
    const [searchBoxInput, setSearchBoxInput] = useState<string>('');
    const [searchButtonDisabled, setDisableSearchButton] = useState(false);
    const [submitButtonDisabled, setDisableSubmitButton] = useState(true);
    const [showUserNotFoundScreen, setUserNotFoundScreen] = useState(false);
    const [showNetworkErrorScreen, setNetworkErrorScreen] = useState(false);
    const [resetButtonDisabled, setResetButtonDisabled] = useState(false);

    const [showDialog, { toggle: toggleHideDialog }] = useBoolean(true);
    const [dialogMessage, setDialogMessage] = useState<IGenericDialogTemplate>();
    const [showModal, { toggle: toogleShowModal }] = useBoolean(false);
    const [responseMessage, setResponseMessage] = useState<AccessRequestResult>();

    const [selectedGridData, setSelectedGridData] = React.useState<{ items: any[] }>({ items: [] });
    const [showGrid, setShowGrid] = useState(false);

    var errorScreenBody: IErrorStateComponentProps | undefined;

    const selection = useRef(new Selection({
        // getKey: (item: any, index?: number): string => {
        //     return item.id;
        // },
        canSelectItem: (item: Role, index?: number) => {
            return !item.isAssigned;
        },
        onSelectionChanged: () => {
            setSelectedGridData({ items: selection.getSelection() ?? [] });
            setDisableSubmitButton(selection.getSelectedCount() === 0);
            setResetButtonDisabled(selection.getSelectedCount() === 0);
        }
    })).current;

    const _onSearchClicked = () => {
        console.log('HARISH ---  Search clicked');
        setShowIndicator(true);
        setUserNotFoundScreen(false);
        setNetworkErrorScreen(false);
        setRoleList([]);
        props.iamService.searchUser(searchBoxInput)
            .then(result => {
                _onSearchDone(result);
            })
            .catch(error => {
                if (error instanceof SearchUserNotFoundError) {
                    // Show user not found screen
                    _onSearchFail();
                }
                _showGenericErrorMessage(error);
            });
    }

    const _onResetClicked = () => {
        selection.setAllSelected(false); // Deselect all rows in the Selection object
        setSelectedGridData({ items: [] }); // Clear local selected items state
    }

    const _onSubmitClicked = () => {
        const selectedRoles = selectedGridData.items as Role[];

        if (!userBaseInformation) {
            throw Error();
        }

        setShowIndicator(true);
        setDisableSearchButton(true);
        props.iamService.assignRolesToUser(userBaseInformation.userEmail, selectedRoles?.filter(r => r.id).map(r => r.id!) ?? [])
            .then(result => {
                setShowIndicator(false);
                setResponseMessage(result);
                toogleShowModal();
                setDisableSearchButton(false);
            })
            .catch(error => {
                setShowIndicator(false);

                var errorMessage = ``;
                if (error instanceof GenericResponseError) {
                    var responseError = error as GenericResponseError;
                    errorMessage = `Failed to submit role assignment request. ${responseError.message}. Error status code: ${responseError.code}. Correlation Id: ${responseError.correlationId}.`
                }
                else {
                    errorMessage = `Failed to submit role assignment request. ${error.message}.`
                }

                setDialogMessage({
                    title: `Failed To Submit Role Assignment Request`,
                    actionType: 'Role Assignment',
                    messageBody: errorMessage,
                    isError: true
                });
                toggleHideDialog();
                setDisableSubmitButton(false);
                setDisableSearchButton(false);
            });
    }

    const _onSearchDone = (userProfile: SearchUserResult) => {
        setShowIndicator(false);
        setDisableSubmitButton(true);
        setUserInformation(userProfile);
        _loadRoles(userProfile.userId);
    }

    const _onSearchFail = () => {
        setShowIndicator(false);
        setUserNotFoundScreen(true);
    }

    const _loadRoles = (userId: string) => {
        setShowIndicator(true);
        props.iamService.getRolesForUser(userId)
            .then(result => {
                _onRolesFetched(result);
            });
    }

    const _onRolesFetched = (roles: Role[]) => {
        setShowIndicator(false);
        setRoleList(roles);
        setShowGrid(roles.length > 0); // Show the grid only if there are selected items
    }

    const _onSearchTextChange = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchBoxInput(e.currentTarget.value);
    }

    const _showGenericErrorMessage = (error: Error) => {
        errorScreenBody = ErrorScreenTemplate.getNetworkError(error);
        setNetworkErrorScreen(true);
    }

    const _ariaLabelForRowItem = (item: Role) => {
        return `Role name ${item.name}, role type ${item.type}, please press Ctrl + Space to select or unselect this role.`;
    }

    return (
        <>
            <div>
                <ScrollablePane>
                    <Stack tokens={tokens.stackTokens} className={SiteStyle._Styles.mainAreaStyle}>
                        <h1>User Role Assignment</h1>
                        <Stack horizontal wrap tokens={tokens.stackTokens} verticalAlign='end' styles={stackStyles}>
                            <TextField style={emailInputBoxStyle} label="Enter the user's email" data-testid="textSearchUser" placeholder="username@contoso.com" onChange={(e) => _onSearchTextChange(e)} />
                            <Stack horizontal tokens={tokens.stackTokens} verticalAlign='center' style={{ marginBottom: 0 }}>
                                <PrimaryButton text='Search' data-testid="btnSearchUser" onClick={_onSearchClicked} disabled={searchButtonDisabled || searchBoxInput.length === 0}></PrimaryButton>
                                {
                                    shouldShowIndicator &&
                                    <Spinner size={SpinnerSize.medium} labelPosition='right' />
                                }
                            </Stack>
                        </Stack>
                        <div role="status">
                            {
                                !shouldShowIndicator &&
                                !showUserNotFoundScreen &&
                                !showNetworkErrorScreen &&
                                userBaseInformation &&
                                <div>
                                    <div>User Name: {userBaseInformation.userName}</div>
                                    <br></br>
                                    <div>
                                        Select the functional roles to grant. For keyboard users, please use Ctrl + Space to select or unselect roles.
                                    </div>
                                </div>
                            }
                        </div>

                        <div role="grid" hidden={!showGrid}>
                            {
                                !showUserNotFoundScreen &&
                                !showNetworkErrorScreen &&
                                <div style={{ padding: '15px', height: 560 }}>
                                    <DataGrid
                                        columnManagementSettings={{
                                            isPanelEnabled: true
                                        }}
                                        data={roleList}
                                        onDataSorted={(sortedData: Role[]) => {
                                            setRoleList(sortedData);
                                        }}
                                        isScrollable={true}
                                        isSortable={true}
                                        listConfig={columns}
                                        selection={selection}
                                        selectionMode={2} // Change option to view different selection modes 2 is for multi select
                                        selectionPreservedOnEmptyClick={true}
                                        setKey={'DATA_GRID_SORTABLE_SELECTABLE'}
                                        checkButtonAriaLabel='Select row'
                                        ariaLabelForSelectAllCheckbox='Select all rows'
                                        getRowAriaLabel={_ariaLabelForRowItem}
                                    >
                                    </DataGrid>
                                </div>
                            }
                        </div>
                        <Stack horizontal tokens={tokens.stackTokens} verticalAlign="center" styles={buttonStackStyles}>
                            <PrimaryButton text='Submit' data-testid="btnSubmitAssignRole" onClick={_onSubmitClicked} disabled={submitButtonDisabled}></PrimaryButton>
                            <DefaultButton text='Reset' type='reset' onClick={_onResetClicked} disabled={resetButtonDisabled} ariaDescription="Unselect all the roles from the available role list" />
                        </Stack>
                        {
                            <div>
                                <AdminPortalFooter />
                            </div>
                        }
                    </Stack>
                    {
                        <div className="custom-error-screen">
                            {showUserNotFoundScreen && <ErrorStateComponent {...ErrorScreenTemplate.userNotFound} />}
                        </div>
                    }
                    {
                        showNetworkErrorScreen &&
                        <ErrorStateComponent {...errorScreenBody} />
                    }
                    <br></br>
                </ScrollablePane>
            </div>
            {
                responseMessage &&
                <RequestConfirmationModal
                    isOpen={showModal}
                    requestResponse={responseMessage}
                    onDismiss={toogleShowModal}
                    userEmail={userBaseInformation?.userEmail}
                />
            }
            {
                dialogMessage &&
                <ActionConfirmationDialog
                    dialogData={dialogMessage}
                    showDialog={showDialog}
                    onDismiss={toggleHideDialog}
                />
            }
        </>
    );
}